import React, { useState, useEffect, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { getUserSelector } from '../../../../../../redux/reducers/reducerSlices/UserReducer';
import { getWarehouseDataReducer } from '../../../../../../redux/reducers/reducerSlices/GetWarehouseDataReducer';
import { getDmsBucketSelector, resetState } from '../../../../../../redux/reducers/reducerSlices/DmsBucketReducer';
import { AppConstant } from '../../../../../Assests/AppConstant';
import { showAlert } from '../../../../../Assests/Utility';
import { sagaActions } from '../../../../../../sagas/sagaActions';
import CustomButtonSS from '../../../../Common/CustomButton/CustomButtonSS';
import SSPopup from '../../../../Common/Popup/SSPopup/SSPopup';
import CustomFieldWithTtileSS from '../../../../Common/CustomField/CustomFieldWithTtileSS';
import WarehouseSearchSS from '../../../../SuperWarehouse/Components/SearchWarehouse/WarehouseSearchSS';
import DmsBucketList from './Components/DmsBucketList';
import _ from 'lodash';
import './ManageDmsBucketDetails.css';
import Popup from '../../../../Common/Popup/Popup';

const ManageDmsBucketDetails = (props) => {
    const { themeSelected } = props;
    const dispatch = useDispatch();
    const user = useSelector(getUserSelector);
    const { cu_id, selectedWarehouse, userType } = user;
    const warehouseData = useSelector(getWarehouseDataReducer);
    const dmsBucketState = useSelector(getDmsBucketSelector);

    const [showModal, setShowModal] = useState(false);
    const [searchStr, setSearchStr] = useState('');
    const [editData, setEditData] = useState({});
    const [warehouseList, setWarehouseList] = useState([]);
    const [startDateState, setStartDateState] = useState('');
    const [endDateState, setEndDateState] = useState('');
    const [startTimeState, setStartTimeState] = useState('');
    const [endTimeState, setEndTimeState] = useState('');

    const [inputs, setInputs] = useState({
        WarehouseID: '',
        WarehouseName: '',
        StartDate: '',
        EndDate: '',
        StartTime: '',
        EndTime: ''
    });

    const [errors, setErrors] = useState({
        WarehouseID: '',
        StartDate: '',
        EndDate: '',
        StartTime: '',
        EndTime: ''
    });

    const [dirty, setDirty] = useState({
        WarehouseID: false,
        StartDate: false,
        EndDate: false,
        StartTime: false,
        EndTime: false
    });

    useEffect(() => {
        if (userType === AppConstant.userPermission.usertypes.superAdmin ||
            userType === AppConstant.userPermission.usertypes.administrator) {
            getWareHouseList();
        } else {
            setInputs(prev => ({
                ...prev,
                WarehouseID: selectedWarehouse.ID,
                WarehouseName: selectedWarehouse.Name
            }));
        }
        fetchBucketList();
    }, []);

    useEffect(() => {
        if (warehouseData.data && warehouseData.data.length > 0) {
            const uniqueData = _.uniqBy(warehouseData.data, 'ID');
            const orderedData = _.orderBy(uniqueData, ['WarehouseName'], ['asc']);
            setWarehouseList(orderedData);
        }
    }, [warehouseData.data]);

    // Handle success/error states from Redux
    useEffect(() => {
        if (dmsBucketState.success && dmsBucketState.message) {
            showAlert(toast.TYPE.SUCCESS, dmsBucketState.message, themeSelected);
            setShowModal(false);
            resetForm();
            dispatch(resetState());
            fetchBucketList();
        }
        if (dmsBucketState.error) {
            // Error should already be a string from the saga, but handle any edge cases
            const errorMsg = typeof dmsBucketState.error === 'string'
                ? dmsBucketState.error
                : 'Operation failed';
            
            showAlert(toast.TYPE.ERROR, errorMsg, themeSelected);
            dispatch(resetState());
        }
    }, [dmsBucketState.success, dmsBucketState.error, dmsBucketState.message]);

    const getWareHouseList = () => {
        dispatch({
            type: sagaActions.GET_WAREHOUSE_LIST,
            payload: { user_type: null, cu_id: null, company_id: null }
        });
    };

    const fetchBucketList = () => {
        const payload = {
            type: 'get'
        };

        dispatch({
            type: sagaActions.FETCH_DMS_BUCKET_LIST,
            payload: payload
        });
    };

    const handleChange = (data, name) => {
        if (name === 'WarehouseID') {
            const selectedWh = warehouseList.find(wh => wh.ID === data);
            setInputs(prev => ({
                ...prev,
                WarehouseID: data,
                WarehouseName: selectedWh?.WarehouseName || ''
            }));
        } else {
            setInputs(prev => ({ ...prev, [name]: data }));
        }
        setDirty(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleTimeChange = (time, name) => {
        if (!time) return;

        const formattedTime = moment(time, 'HH:mm').format('HH:mm');

        setInputs(prev => ({ ...prev, [name]: formattedTime }));
        setDirty(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleDateChange = (date, name) => {
        // Format the selected date to 'YYYY-MM-DD'
        const formattedDate = moment(date).format('YYYY-MM-DD');

        // Update the specific date state
        if (name === 'StartDate') {
            setStartDateState(formattedDate);
        } else if (name === 'EndDate') {
            setEndDateState(formattedDate);
        }

        // Update the inputs state with the new date value
        setInputs(prev => ({ ...prev, [name]: formattedDate }));
        setDirty(prev => ({ ...prev, [name]: true }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleErrors = (name) => {
        setDirty(prev => ({ ...prev, [name]: true }));
    };

    const validate = () => {
        let valid = true;
        let errorData = { ...errors };

        if (!inputs.WarehouseID) {
            errorData.WarehouseID = 'Warehouse is required';
            valid = false;
        }
        if (!inputs.StartDate) {
            errorData.StartDate = 'Start Date is required';
            valid = false;
        }
        if (!inputs.EndDate) {
            errorData.EndDate = 'End Date is required';
            valid = false;
        }
        if (!inputs.StartTime) {
            errorData.StartTime = 'Start Time is required';
            valid = false;
        }
        if (!inputs.EndTime) {
            errorData.EndTime = 'End Time is required';
            valid = false;
        }

        // Validate that End Date is after Start Date
        if (inputs.StartDate && inputs.EndDate) {
            const startMoment = moment(inputs.StartDate);
            const endMoment = moment(inputs.EndDate);
            if (startMoment.isAfter(endMoment)) {
                errorData.EndDate = 'End Date must be after Start Date';
                valid = false;
            }
        }

        // Validate that End Time is after Start Time when both dates are the same
        if (inputs.StartDate && inputs.EndDate && inputs.StartTime && inputs.EndTime) {
            const isSameDate = moment(inputs.StartDate).isSame(moment(inputs.EndDate), 'day');
            if (isSameDate) {
                const startTime = moment(inputs.StartTime, 'HH:mm');
                const endTime = moment(inputs.EndTime, 'HH:mm');

                // If End Time is before or equal to Start Time, show an error
                if (endTime.isSameOrBefore(startTime)) {
                    errorData.EndTime = 'End Time must be after Start Time';
                    valid = false;
                }
            }
        }

        setErrors(errorData);
        return valid;
    };

    const handleSubmit = () => {
        Object.keys(dirty).forEach(key => {
            setDirty(prev => ({ ...prev, [key]: true }));
        });

        if (!validate()) return;

        const payload = editData.Id ? {
            type: 'edit',
            Id: editData.Id,
            WarehouseID: inputs.WarehouseID,
            WarehouseName: inputs.WarehouseName,
            StartDate: inputs.StartDate,
            EndDate: inputs.EndDate,
            StartTime: inputs.StartTime,
            EndTime: inputs.EndTime,
            UpdatedBy: cu_id
        } : {
            type: 'create',
            Id: null,
            WarehouseID: inputs.WarehouseID,
            WarehouseName: inputs.WarehouseName,
            StartDate: inputs.StartDate,
            EndDate: inputs.EndDate,
            StartTime: inputs.StartTime,
            EndTime: inputs.EndTime,
            CreatedBy: cu_id
        };

        dispatch({
            type: sagaActions.MANAGE_DMS_BUCKET,
            payload: payload
        });
    };

    const handleEdit = (data) => {
        setEditData(data);
        setInputs({
            WarehouseID: data.WarehouseID,
            WarehouseName: data.WarehouseName,
            StartDate: moment(data.StartDate).format('YYYY-MM-DD'),
            EndDate: moment(data.EndDate).format('YYYY-MM-DD'),
            StartTime: data.StartTime
                ? moment(data.StartTime, "HH:mm:ss").format("HH:mm")
                : "",
            EndTime: data.EndTime
                ? moment(data.EndTime, "HH:mm:ss").format("HH:mm")
                : ""
        });
        setStartDateState(moment(data.StartDate).format('YYYY-MM-DD'));
        setEndDateState(moment(data.EndDate).format('YYYY-MM-DD'));
        setStartTimeState(data.StartTime);
        setEndTimeState(data.EndTime);
        setShowModal(true);
    };

    const getStatusValue = (status) => {
        if (status?.data && Array.isArray(status.data)) {
            return status.data[0] === 1;
        }
        return Boolean(status);
    };

    const handleEnableDisable = (data) => {
        const isActive = getStatusValue(data.Status);

        const payload = {
            type: isActive ? 'disable' : 'enable',
            Id: data.Id,
            UpdatedBy: cu_id
        };

        dispatch({
            type: sagaActions.MANAGE_DMS_BUCKET,
            payload: payload
        });
    };

    const handleDelete = (data) => {
        const payload = {
            type: 'delete',
            Id: data.Id,
            UpdatedBy: cu_id
        };

        dispatch({
            type: sagaActions.MANAGE_DMS_BUCKET,
            payload: payload
        });
    };

    const resetForm = () => {
        setInputs({
            WarehouseID: userType === AppConstant.userPermission.usertypes.superAdmin ||
                userType === AppConstant.userPermission.usertypes.administrator
                ? '' : selectedWarehouse.ID,
            WarehouseName: userType === AppConstant.userPermission.usertypes.superAdmin ||
                userType === AppConstant.userPermission.usertypes.administrator
                ? '' : selectedWarehouse.Name,
            StartDate: '',
            EndDate: '',
            StartTime: '',
            EndTime: ''
        });
        setErrors({});
        setDirty({});
        setEditData({});
        setStartDateState('');
        setEndDateState('');
        setStartTimeState('');
        setEndTimeState('');
    };

    const closepopup = () => {
        resetForm();
        setShowModal(false);
    };

    const TUSDatePickerInput = forwardRef(({ value, titleField, onClick }, ref) => {
        const displayValue = titleField === 'Start'
            ? (startTimeState || inputs.StartTime || 'HH:MM')
            : (endTimeState || inputs.EndTime || 'HH:MM');
        return (
            <button className="customDatePickerInput-ss" onClick={onClick} ref={ref}>
                {displayValue}
            </button>
        );
    });

    const createWarehouseListDD = () => {
        return warehouseList.map((data) => (
            <option key={data.ID} value={data.ID}>
                {data.WarehouseName}
            </option>
        ));
    };

    const createForm = () => {
        return (
            <div className='d-flex flex-column gap-2'>
                {(userType === AppConstant.userPermission.usertypes.superAdmin ||
                    userType === AppConstant.userPermission.usertypes.administrator) && (
                        <CustomFieldWithTtileSS
                            type="select"
                            showError={dirty.WarehouseID && errors.WarehouseID ? errors.WarehouseID : ''}
                            name="WarehouseID"
                            value={inputs.WarehouseID}
                            handleErrors={handleErrors}
                            title="Warehouse"
                            required={true}
                            onChange={handleChange}
                            listData={createWarehouseListDD}
                            disabledField={Object.keys(editData).length > 0}
                        />
                    )}

                <div className='d-flex gap-2'>
                    <div className='flex-fill'>
                        <div className="inputTitle">
                            Start Date <span className="color-red">*</span>
                        </div>
                        <input
                            type="date"
                            name="StartDate"
                            className='w-100 customTextInput'
                            value={inputs.StartDate}
                            onBlur={() => handleErrors('StartDate')}
                            onChange={(e) => handleDateChange(e.target.value, e.target.name)}
                            min={moment().format("YYYY-MM-DD")}
                        />
                        {dirty.StartDate && errors.StartDate && (
                            <span className="errorMessage">{errors.StartDate}</span>
                        )}
                    </div>
                    <div className='flex-fill'>
                        <div className="inputTitle">
                            End Date <span className="color-red">*</span>
                        </div>
                        <input
                            type="date"
                            name="EndDate"
                            className='w-100 customTextInput'
                            value={inputs.EndDate}
                            onBlur={() => handleErrors('EndDate')}
                            onChange={(e) => handleDateChange(e.target.value, e.target.name)}
                            min={inputs.StartDate || moment().format("YYYY-MM-DD")}
                        />
                        {dirty.EndDate && errors.EndDate && (
                            <span className="errorMessage">{errors.EndDate}</span>
                        )}
                    </div>
                </div>

                <div className='d-flex gap-2'>
                    <div className='flex-fill'>
                        <CustomFieldWithTtileSS
                            type="custom-time-selector"
                            showError={dirty.StartTime && errors.StartTime ? errors.StartTime : ''}
                            name="StartTime"
                            customDatePickerConfig={{
                                selectedDate: inputs.StartTime ? moment(inputs.StartTime, 'HH:mm').toDate() : '',
                                name: 'StartTime',
                                timeFormat: 'HH:mm',
                                showTimeSelect: true,
                                showTimeSelectOnly: true,
                                timeIntervals: 15,
                                timeCaption: 'Time',
                                customInput: <TUSDatePickerInput titleField="Start" />
                            }}
                            value={inputs.StartTime}
                            handleErrors={handleErrors}
                            title="Start Time"
                            required={true}
                            onChange={(date) => handleTimeChange(date, 'StartTime')}

                        />
                    </div>
                    <div className='flex-fill'>
                        <CustomFieldWithTtileSS
                            type="custom-time-selector"
                            showError={dirty.EndTime && errors.EndTime ? errors.EndTime : ''}
                            name="EndTime"
                            customDatePickerConfig={{
                                selectedDate: inputs.EndTime ? moment(inputs.EndTime, 'HH:mm').toDate() : '',
                                name: 'EndTime',
                                timeFormat: 'HH:mm',
                                showTimeSelect: true,
                                showTimeSelectOnly: true,
                                timeIntervals: 15,
                                timeCaption: 'Time',
                                customInput: <TUSDatePickerInput titleField="End" />
                            }}
                            value={inputs.EndTime}
                            handleErrors={handleErrors}
                            title="End Time"
                            required={true}
                            onChange={(date) => handleTimeChange(date, 'EndTime')}
                        />
                    </div>
                </div>

                <div className="d-flex justify-content-end my-2 gap-2">
                    <CustomButtonSS
                        isLoading={false}
                        title="Reset"
                        className="btn-unstyled"
                        onClick={resetForm}
                    />
                    <CustomButtonSS
                        isLoading={dmsBucketState.loading}
                        title="Save"
                        className="btn-styled"
                        onClick={handleSubmit}
                    />
                </div>
            </div>
        );
    };

    const showCreateBucketPopup = () => {
        const popupObj = {
            id: 'createDmsBucketPopup',
            modalLabel: 'createDmsBucketPopup',
            showClose: true,
            showfooter: false,
            showHeader: true,
            modalSize: 'md',
            title: Object.keys(editData).length > 0 ? 'Edit DMS Bucket' : 'Create DMS Bucket',
            bodyText: createForm(),
            subText: "",
            keyboard: false,
            backdrop: 'static',
        };

        return (
            <Popup
                {...popupObj}
                closepopup={closepopup}
                themeSelected={themeSelected}
                showModal={showModal}
            />
        );
    };

    return (
        <div className="container-fluid">
            <div className='d-flex py-2 text-start justify-content-between mt-1'>
                <button
                    className='addButton'
                    onClick={() => setShowModal(true)}
                >
                    {AppConstant.commonStrings.create}
                </button>
                <div className='ms-5'>
                    <WarehouseSearchSS
                        placeholder='Search Buckets'
                        inputValue={searchStr}
                        cancelSearch={() => setSearchStr('')}
                        onChangeHandler={(text) => setSearchStr(text)}
                    />
                </div>
            </div>

            <div className="mt-2 d-flex justify-content-center dmsBucketListTableContainer">
                <DmsBucketList
                    bucketList={dmsBucketState.data}
                    searchStr={searchStr}
                    onEdit={handleEdit}
                    onEnableDisable={handleEnableDisable}
                    onDelete={handleDelete}
                    themeSelected={themeSelected}
                    loading={dmsBucketState.loading}
                />
            </div>

            {showCreateBucketPopup()}
        </div>
    );
};

export default ManageDmsBucketDetails;